import { CsobFormMetaDataDto } from "@green0meter/api-client";
import React, { FC, useEffect, useState } from "react";
import FeatherIcon from "feather-icons-react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import PdfDocument from "../../../../../PdfDocument";

import { useGetFormDraftData, useGetFormSections } from "@api";
import { IconButton, Tooltip } from "@components/base";
import {
  downloadPdfBlob,
  generatePdfAssetQuestions,
} from "@components/main/csob/hub/Questionnaires";
import { useUserStore } from "@store/user";

interface ExportFormButton {
  form: CsobFormMetaDataDto;
  companyName: string;
}

const ExportFormButton: FC<ExportFormButton> = ({ form, companyName }) => {
  if (!form.formId) {
    // TODO: Fix on BE so formId can't be null
    return null;
  }

  const [loadData, setLoadData] = useState<boolean>(false);

  const { t } = useTranslation();
  const { user } = useUserStore();
  const { formDraftData, isLoadingFormDraftData } = useGetFormDraftData(
    user,
    form.formId,
    loadData,
  );
  const { formSections, isLoadingFormSections } = useGetFormSections(
    user,
    form.formId,
    loadData,
  );

  const handleExport = async (): Promise<void> => {
    setLoadData(true);
  };

  const exportPdf = async (): Promise<void> => {
    if (formSections && formDraftData) {
      const { questions, relevantYear } = generatePdfAssetQuestions(
        formSections,
        formDraftData,
      );

      await downloadPdfBlob({
        fileName: `${companyName}_${form.name}_${dayjs().format("DDMMYYYY")}.pdf`,
        pdfDocument: (
          <PdfDocument
            user={user}
            questions={questions}
            companyName={companyName}
            mainRelevantYear={relevantYear}
          />
        ),
      });
    }
  };

  useEffect(() => {
    if (loadData && formDraftData && formSections) {
      setLoadData(false);
      exportPdf();
    }
  }, [isLoadingFormDraftData, isLoadingFormSections, loadData]);

  return (
    <Tooltip arrow title={t("csob.questionnaires.download")} placement="top">
      <IconButton
        size="small"
        onClick={handleExport}
        color="primary"
        disabled={isLoadingFormDraftData || isLoadingFormSections}
      >
        <FeatherIcon icon="download" size={16} />
      </IconButton>
    </Tooltip>
  );
};

export default ExportFormButton;
